import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  TrendingUp, 
  Shield, 
  Lightbulb, 
  Clock,
  Award,
  FileText,
  Handshake,
  BarChart,
  Lock
} from 'lucide-react';

// Dodanie importu czcionki Inter
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const GrantSalesSection = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: <TrendingUp className="w-6 h-6 text-white" />,
      title: "Analiza potencjału projektowego",
      points: [
        "Identyfikacja optymalnych źródeł finansowania dla Twojego przedsiębiorstwa",
        "Ocena szans na uzyskanie dofinansowania",
        "Dopasowanie koncepcji projektu do wymogów programowych"
      ]
    },
    {
      icon: <FileText className="w-6 h-6 text-white" />,
      title: "Przygotowanie dokumentacji aplikacyjnej",
      points: [
        "Opracowanie biznesplanu i kompleksowej analizy finansowej",
        "Przygotowanie wniosku o dofinansowanie zgodnie z aktualnymi wytycznymi",
        "Kompletowanie niezbędnych załączników i dokumentów uzupełniających"
      ]
    },
    {
      icon: <Handshake className="w-6 h-6 text-white" />,
      title: "Negocjacje i podpisanie umowy",
      points: [
        "Reprezentowanie klienta w kontaktach z instytucjami przyznającymi dofinansowanie",
        "Wsparcie prawne podczas negocjacji warunków umowy",
        "Zabezpieczenie interesów klienta w finalnej dokumentacji projektowej"
      ]
    },
    {
      icon: <BarChart className="w-6 h-6 text-white" />,
      title: "Realizacja i rozliczenie projektu",
      points: [
        "Monitoring zgodności projektu z założeniami i harmonogramem",
        "Przygotowanie raportów i sprawozdań finansowych",
        "Obsługa kontroli i audytów ze strony instytucji nadzorujących"
      ]
    }
  ];

  const advantages = [
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Unikalne połączenie kompetencji prawnych i finansowych",
      description: "Zapewniamy nie tylko skuteczność w pozyskiwaniu środków, ale również bezpieczeństwo prawne realizowanych projektów."
    },
    {
      icon: <Lock className="w-10 h-10" />,
      title: "Gwarancja poufności chroniona tajemnicą adwokacką",
      description: "Wszystkie informacje dotyczące Twojego przedsiębiorstwa i planowanych projektów objęte są najwyższym poziomem ochrony."
    },
    {
      icon: <Lightbulb className="w-10 h-10" />,
      title: "Innowacyjne podejście do projektów rozwojowych",
      description: "Współpracujemy z wiodącymi naukowcami i specjalistami branżowymi, co pozwala nam skutecznie wspierać projekty o wysokim potencjale innowacyjnym."
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: "Optymalizacja procedur i minimalizacja obciążeń",
      description: "Przejmujemy ciężar formalności związanych z aplikowaniem o środki i rozliczaniem projektów, pozwalając Ci skupić się na rozwoju Twojego biznesu."
    }
  ];

  const SectionTitle = ({ children, className = '' }) => (
    <h2 
      className={`text-2xl sm:text-3xl md:text-5xl text-white text-center uppercase leading-tight ${className}`}
      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
    >
      {children}
    </h2>
  );
  
  return (
    <section className="py-16 md:py-24 bg-black">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <SectionTitle className="mb-6">
            Kompleksowe wsparcie na każdym etapie pozyskiwania dofinansowania
          </SectionTitle>
          <p className="text-lg sm:text-xl text-gray-300">
            W Szynkowski & Company oferujemy pełen zakres usług związanych z pozyskiwaniem funduszy europejskich.
          </p>
          <div className="mt-8 inline-flex items-center gap-4 backdrop-blur-md bg-white/5 p-4 rounded-xl border border-white/10 max-w-3xl">
            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-violet-600/20 to-violet-700/20 text-violet-500 rounded-lg">
                <Award className="w-6 h-6" />
            </div>
            <p className="text-gray-300 text-left text-base">
              Nasz zespół ekspertów, z ponad 10-letnim doświadczeniem i ponad <strong className="text-white">100 milionami złotych</strong> rozliczonych dofinansowań, zapewnia profesjonalne wsparcie na każdym etapie procesu.
            </p>
          </div>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
          {steps.map((step, index) => (
            <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl p-5 sm:p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-violet-600 to-violet-700 rounded-lg flex items-center justify-center text-white shadow-lg shadow-violet-600/20">
                  {step.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-4">
                    {step.title}
                  </h3>
                  <ul className="space-y-3">
                    {step.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start gap-3 text-gray-300">
                        <CheckCircle className="w-5 h-5 text-violet-600 flex-shrink-0 mt-1" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Advantages */}
        <div className="mb-24">
          <SectionTitle className="mb-16">
            Dlaczego warto nam zaufać?
          </SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl p-5 sm:p-8 border border-white/10 text-center hover:border-violet-600/50 transition-all duration-300">
                <div className="flex flex-col items-center">
                  <div className="mb-6 w-16 h-16 flex items-center justify-center bg-gradient-to-br from-violet-600/20 to-violet-700/20 text-violet-500 rounded-full">
                    {advantage.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">
                    {advantage.title}
                  </h4>
                  <p className="text-gray-400">
                    {advantage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="inline-block backdrop-blur-md bg-gradient-to-r from-violet-600/10 to-violet-700/10 rounded-xl p-8 md:p-12 max-w-3xl border border-violet-600/20">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Zacznij swoją drogę do sukcesu już dziś
            </h3>
            <p className="text-gray-300 mb-8">
              Skontaktuj się z nami i przekonaj się, jak możemy pomóc w rozwoju Twojego biznesu dzięki funduszom europejskim.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/kontakt')}
                className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white font-semibold rounded-full transition-all duration-300 hover:from-violet-700 hover:to-violet-800 hover:scale-105 text-center"
              >
                <span>Umów bezpłatną konsultację</span>
              </button>
              <button 
                onClick={() => navigate('/o-nas')}
                className="px-8 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-full transition-all duration-300 hover:bg-white/20 hover:border-white/30"
              >
                Poznaj nasz zespół
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrantSalesSection;